import React, { useContext, useMemo, useState } from "react"
import {View, StyleSheet, TouchableOpacity, Dimensions} from "react-native"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { FontAwesome5 } from "@expo/vector-icons"
import { connect } from "react-redux"
import { apiRequest } from "@data/redux/actions/api"
import { skillSetsService } from "@data/services/skillSetsService"
import { tutorsService } from "@data/services/tutorsService"
import { useApiStatus } from "@common/hooks/useApiStatus"
import * as WebBrowser from "expo-web-browser"
import { IS_WEB } from "@common/utils/mobileUtils"
import { useNavigation } from "@react-navigation/native"

const TutorTaskBanner = (props) => {
    const dimensions = useContext(DimensionsContext)
    const navigation = useNavigation()
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0)
    
    // Read tasks from Redux state (cached data shown immediately)
    const tasks = props.tutorTasks

    // Handle Stripe API response
    useApiStatus(
        skillSetsService.getStripeFormUrl,
        null,
        true,
        (data) => {
            if (IS_WEB) {
                window.open(data?.url, "_self")
            } else {
                WebBrowser.openBrowserAsync(data?.url)
            }
        }
    )

    // Define all tasks based on backend data - ORDERED BY PRIORITY
    const taskList = useMemo(() => {
        // Don't build list if no tasks data
        if (!tasks) {
            return []
        }
        
        const list = []

        // PRIORITY 0: Prepare live sessions (ALWAYS FIRST - MOST IMPORTANT)
        if (tasks.prepare_live?.required) {
            list.push({
                id: "prepare_live",
                priority: 0,
                title: "Prépare ton live",
                description:
                    "Choisis les matières et les chapitres pour permettre à Sam de démarrer la session avec chaque élève",
                buttonLabel: "Préparer mon live",
                buttonColor: theme.colors.primary,
                cardColor: theme.colors.warningLight,
                icon: "clipboard-list",
                badgeCount: tasks.prepare_live.count,
                onPress: () => navigation.push("tutor/prepare-live"),
                loading: false,
            })
        }

        // PRIORITY 1: Missing live reports (URGENT)
        if (tasks.missing_reports?.required) {
            list.push({
                id: "missing_live_reports",
                priority: 1,
                title: "Comptes-rendus de lives manquants",
                description:
                    "Il manque des comptes-rendus de lives. Merci de les compléter rapidement !",
                buttonLabel: "Compléter",
                buttonColor: theme.colors.accent,
                cardColor: theme.colors.accentLighter,
                icon: "exclamation-triangle",
                badgeCount: tasks.missing_reports.count,
                onPress: () =>
                    navigation.push("end-session-tutor", {
                        occurrence_id: tasks.missing_reports.last_occurrence_id,
                    }),
                loading: false,
            })
        }

        // PRIORITY 2: Keep availability fresh (« Ma dispo » — nudge to re-confirm
        // the semaine-type once the 7-day freshness horizon lapses)
        if (tasks.update_availability?.required) {
            list.push({
                id: "update_availability",
                priority: 2,
                // Same verb and same object as « Ma dispo » itself: the tutor
                // performs ONE gesture, so every surface that asks for it says
                // « confirmer » and « tes créneaux ».
                title: "Confirme tes créneaux",
                description:
                    "Une fois par semaine, pour continuer à recevoir des propositions de groupes.",
                buttonLabel: "Confirmer",
                buttonColor: theme.colors.primary,
                cardColor: theme.colors.warningLight,
                icon: "calendar-check",
                onPress: () => navigation.push("tutor/ma-dispo"),
                loading: false,
            })
        }

        // PRIORITY 3: Complete profile
        if (tasks.complete_profile?.required) {
            list.push({
                id: "complete_profile",
                priority: 3,
                title: "Complète ton profil de tuteur",
                description:
                    "Indique tes informations de profil pour commencer à prendre des groupes de Lives !",
                buttonLabel: "Compléter",
                buttonColor: theme.colors.primary,
                cardColor: theme.colors.successLight,
                icon: "user-circle",
                onPress: () => navigation.push("tutor/complete-profile"),
                loading: false,
            })
        }

        // PRIORITY 4: Register Stripe account
        if (tasks.stripe_account?.required) {
            list.push({
                id: "stripe_account",
                priority: 4,
                title: "Enregistre ton compte Stripe",
                description: "Cela te permettra de recevoir tes paiements",
                buttonLabel: "Je m'enregistre",
                buttonColor: theme.colors.success,
                cardColor: theme.colors.successLight,
                icon: "credit-card",
                onPress: () =>
                    props.apiRequest(
                        skillSetsService.getStripeFormUrl,
                        {},
                        {},
                        { email: props.user.email }
                    ),
                loading: props.stripeRequest,
            })
        }

        // The training video + quiz is not a task here: it has its own home
        // card (TrainingCompletionCard), which replaces this entry.

        return list
    }, [tasks, props.stripeRequest, props.user])

    // Reset to first task if current index is out of bounds
    React.useEffect(() => {
        if (currentTaskIndex >= taskList.length && taskList.length > 0) {
            setCurrentTaskIndex(0)
        }
    }, [taskList.length, currentTaskIndex])

    // Get current active task
    const currentTask = taskList[currentTaskIndex] || taskList[0]

    // Don't render if loading AND no cached data, or no tasks at all
    const isLoading = props.tutorTasksLoading
    const hasCachedData = tasks != null
    
    // Only show loading if we're loading AND we have no cached data
    if ((isLoading && !hasCachedData) || !currentTask || taskList.length === 0) {
        return null
    }

    const handleNextTask = () => {
        if (currentTaskIndex < taskList.length - 1) {
            setCurrentTaskIndex(currentTaskIndex + 1)
        }
    }

    const handlePreviousTask = () => {
        if (currentTaskIndex > 0) {
            setCurrentTaskIndex(currentTaskIndex - 1)
        }
    }

    return (
        <View style={[
            styles.container, 
            { 
                backgroundColor: currentTask.cardColor,
                width: Dimensions.get("window").width,

                marginHorizontal: 10,
            }
        ]}>
            
            <View style={[styles.content,{width:dimensions.width - 20}]}>
                {/* Progress indicator */}
                <View style={styles.progressContainer}>
                    <View style={styles.progressHeader}>
                        <View style={styles.iconContainer}>
                            <FontAwesome5
                                name={currentTask.icon}
                                size={20}
                                color={theme.colors.primary}
                                solid
                            />
                        </View>
                        <View style={styles.progressTextContainer}>
                            <TextM
                                fontSize={12}
                                color={theme.colors.grey}
                                fontWeight="SemiBold"
                            >
                                TÂCHES À COMPLÉTER
                            </TextM>
                            <TextM
                                fontSize={14}
                                color={theme.colors.primary}
                                fontWeight="Bold"
                            >
                                {currentTaskIndex + 1} / {taskList.length}
                            </TextM>
                        </View>
                        <View style={styles.navButtons}>
                            <TouchableOpacity
                                onPress={handlePreviousTask}
                                disabled={currentTaskIndex === 0}
                                style={[
                                    styles.navButton,
                                    currentTaskIndex === 0 && styles.navButtonDisabled,
                                ]}
                            >
                                <FontAwesome5
                                    name="chevron-left"
                                    size={16}
                                    color={
                                        currentTaskIndex === 0
                                            ? theme.colors.lightMore
                                            : theme.colors.primary
                                    }
                                />
                            </TouchableOpacity>

                            <TouchableOpacity
                                onPress={handleNextTask}
                                disabled={currentTaskIndex === taskList.length - 1}
                                style={[
                                    styles.navButton,
                                    currentTaskIndex === taskList.length - 1 &&
                                    styles.navButtonDisabled,
                                ]}
                            >
                                <FontAwesome5
                                    name="chevron-right"
                                    size={16}
                                    color={
                                        currentTaskIndex === taskList.length - 1
                                            ? theme.colors.lightMore
                                            : theme.colors.primary
                                    }
                                />
                            </TouchableOpacity>
                        </View>
                    </View>
                    
                    {/* Progress bar with clickable segments */}
                    <View style={styles.progressBarContainer}>
                        {taskList.map((task, index) => (
                            <TouchableOpacity
                                key={task.id}
                                activeOpacity={0.7}
                                onPress={() => setCurrentTaskIndex(index)}
                                style={[
                                    styles.progressSegment,
                                    {
                                        flex: 1,
                                        backgroundColor:
                                            index === currentTaskIndex
                                                ? theme.colors.primary
                                                : index < currentTaskIndex
                                                ? theme.colors.grey
                                                : theme.colors.lightMore,
                                        marginLeft: index > 0 ? 4 : 0,
                                    },
                                ]}
                            />
                        ))}
                    </View>
                </View>

                <View style={styles.taskContent}>
                    <View style={styles.taskHeader}>
                        <TextM
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            wrap
                            fontSize={18}
                            style={styles.title}
                        >
                            {currentTask.title}
                        </TextM>
                        {currentTask.badgeCount && currentTask.badgeCount > 0 && (
                            <View style={styles.badge}>
                                <TextM
                                    fontSize={14}
                                    color={theme.colors.white}
                                    fontWeight="Bold"
                                >
                                    {currentTask.badgeCount}
                                </TextM>
                            </View>
                        )}
                    </View>
                    <TextM
                        wrap
                        style={styles.description}
                        color={theme.colors.primary}
                    >
                        {currentTask.description}
                    </TextM>
                </View>

                <View style={styles.buttonContainer}>
                    <FilledButtonM
                        style={{
                            width:
                                dimensions.width > 800
                                    ? dimensions?.width / 3
                                    : null,
                        }}
                        color={currentTask.buttonColor}
                        label={currentTask.buttonLabel}
                        loading={currentTask.loading || props.tutorTasksLoading}
                        disabled={currentTask.loading || props.tutorTasksLoading}
                        onPress={currentTask.onPress}
                    />
                </View>


            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        paddingVertical: 14,
        paddingHorizontal: 16,
        justifyContent: "center",
        position: "relative",
        marginTop: 0,
        shadowOpacity: 0.1,
        elevation: 1,
    },
    badge: {
        backgroundColor: theme.colors.error,
        borderRadius: 12,
        minWidth: 24,
        height: 24,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 8,
        marginLeft: 10,
        zIndex: 10,
    },
    content: {
        flex: 1,
        width: "90%",
        alignSelf: 'center',
    },
    progressContainer: {
        marginBottom: 12,
    },
    progressHeader: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 8,
    },
    iconContainer: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 10,
    },
    progressTextContainer: {
        flex: 1,
    },
    progressBarContainer: {
        flexDirection: "row",
        height: 6,
        borderRadius: 3,
        overflow: "hidden",
    },
    progressSegment: {
        height: 6,
        borderRadius: 3,
    },
    taskContent: {
        marginBottom: 12,
        minHeight: 80,
    },
    taskHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        flexWrap: "wrap",
        marginBottom: 6,
        minHeight: 40,
    },
    title: {
        flex: 1,
        maxWidth: "85%",
    },
    urgentBadge: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.error,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        marginLeft: 8,
    },
    priorityBadge: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.warning,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        marginLeft: 8,
    },
    description: {
        minHeight: 42,
        lineHeight: 19,
    },
    buttonContainer: {
        alignSelf: "flex-start",
    },

    navigationContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 16,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
    },
    remainingTasksContainer: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
    },
    navButtons: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    navButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: theme.colors.primary,
    },
    navButtonDisabled: {
        borderColor: theme.colors.lightMore,
        opacity: 0.5,
    },
})

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        tutorTasks: state.data.tutors?.tasks,
        tutorTasksLoading: state.api.getTutorTasks?.request || false,
        stripeRequest: state.api.getStripeFormUrl?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(TutorTaskBanner)
